import React from 'react';
import { X, Star, CheckSquare, AlignLeft, List } from 'lucide-react';
import { Question } from '@/services/PagesServices/FeedbackManagementService';
import './ViewQuestionsModal.css';

interface ViewQuestionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  surveyName: string;
  questions: Question[];
}

const ViewQuestionsModal: React.FC<ViewQuestionsModalProps> = ({
  isOpen,
  onClose,
  surveyName,
  questions
}) => {
  if (!isOpen) return null;

  const getQuestionTypeLabel = (type: string): string => {
    switch (type) {
      case 'short_text':
        return 'Short Text';
      case 'paragraph':
        return 'Paragraph';
      case 'multiple_choice_single':
        return 'Multiple Choice (Single)';
      case 'multiple_choice_multi':
        return 'Multiple Choice (Multi)';
      case 'star_rating':
        return 'Star Rating';
      default:
        return type;
    }
  };

  const getQuestionTypeIcon = (type: string) => {
    switch (type) {
      case 'short_text':
      case 'paragraph':
        return <AlignLeft size={16} />;
      case 'multiple_choice_single':
        return <List size={16} />;
      case 'multiple_choice_multi':
        return <CheckSquare size={16} />;
      case 'star_rating':
        return <Star size={16} />;
      default:
        return null;
    }
  };

  const renderQuestion = (question: Question, index: number) => {
    return (
      <div key={question.id || index} className="view-questions-item">
        <div className="view-questions-item-header">
          <div className="view-questions-item-number">Q{index + 1}</div>
          <div className="view-questions-item-type">
            {getQuestionTypeIcon(question.type)}
            <span>{getQuestionTypeLabel(question.type)}</span>
          </div>
          {question.required && (
            <span className="view-questions-required-badge">Required</span>
          )}
        </div>

        <div className="view-questions-item-text">{question.text}</div>

        {question.type === 'short_text' && (
          <div className="view-questions-preview">
            <input
              type="text"
              className="view-questions-preview-input"
              placeholder="Short answer text"
              disabled
            />
          </div>
        )}

        {question.type === 'paragraph' && (
          <div className="view-questions-preview">
            <textarea
              className="view-questions-preview-textarea"
              placeholder="Long answer text"
              rows={3}
              disabled
            />
          </div>
        )}

        {question.type === 'star_rating' && (
          <div className="view-questions-preview">
            <div className="view-questions-stars">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star key={star} size={24} className="view-questions-star-icon" />
              ))}
            </div>
          </div>
        )}

        {question.type === 'multiple_choice_single' && (
          <div className="view-questions-preview">
            <div className="view-questions-options">
              {(question.options || ['Option 1', 'Option 2']).map((option, optIndex) => (
                <label key={optIndex} className="view-questions-option">
                  <input type="radio" name={`question-${index}`} disabled />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {question.type === 'multiple_choice_multi' && (
          <div className="view-questions-preview">
            <div className="view-questions-options">
              {(question.options || ['Option 1', 'Option 2']).map((option, optIndex) => (
                <label key={optIndex} className="view-questions-option">
                  <input type="checkbox" disabled />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="view-questions-modal-overlay" onClick={onClose}>
      <div className="view-questions-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="view-questions-modal-header">
          <div>
            <h3>Survey Questions</h3>
            <p className="view-questions-survey-name">{surveyName}</p>
          </div>
          <button className="view-questions-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="view-questions-modal-body">
          {questions.length === 0 ? (
            <div className="view-questions-empty">
              <p>No questions found in this survey</p>
            </div>
          ) : (
            <div className="view-questions-list">
              {questions.map((question, index) => renderQuestion(question, index))}
            </div>
          )}
        </div>

        <div className="view-questions-modal-footer">
          <div className="view-questions-stats">
            Total Questions: <strong>{questions.length}</strong>
          </div>
          <button className="view-questions-close-footer-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewQuestionsModal;
